import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { CircularProgress } from '@material-ui/core';
import { ArrowBack } from '@material-ui/icons';
import {
  fullloader,
  setProgressType,
  toast,
  updateNavProgressMeter,
  updateProgressMeterResults,
} from '../../../redux/action';
import { getMsgBy_Id, getMessageEventInfo } from '../../../redux/services';
import {
  fetchOrgGroups,
  getOrganizationTopics,
} from '../../../redux/services/groups';
import { selectedOrganizationSelector } from '../../../redux/selector';
import { createIdNameMappingFromData } from '../../../utils';
import { useMessageV2Query, useMessageDailyV2Query } from '../api';
import { useTableState } from '../tableState';
import useStatDrawers from '../hooks/useStatDrawers';
import { CHANNEL_TILES, getTileValues } from '../utils/rates';
import {
  MAX_RANGE_DAYS,
  getDailySeries,
  getDayRange,
  getDefaultRange,
  getMetricsForChannel,
  getResendLabel,
  isOutsideMaxRange,
} from '../utils/graph';
import { getSingleMessageExport } from '../utils/export';
import { BLUE, Page } from '../AnalyticsV2.styled';
import { LinkButton, Placeholder } from './MessageDetail.styled';
import Header from './Header';
import EngagementGraph from './EngagementGraph';
import StatTiles from './StatTiles';

// "No data yet" / "No data" in place of the graph, or null.
const getGraphNoDataText = message => {
  if (message.status === 'scheduled') return 'No data yet';
  if (message.status === 'cancelled' && !message.smartResend) return 'No data';
  return null;
};

// The detail page for one message: header, engagement graph by day, and the
// stat tiles. tablePath: the message table's URL, for "Back to Messages".
const MessageDetail = ({ tablePath }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { messageId } = useParams();
  const organization = useSelector(selectedOrganizationSelector);
  const { orgId } = useTableState();
  const { openTileDrawer, drawer } = useStatDrawers();

  const [channel, setChannel] = useState('all');
  const [metrics, setMetrics] = useState(['open']);
  const [chartType, setChartType] = useState('line');
  // The picked range, or null for the default (send date + 29 days).
  const [pickedRange, setPickedRange] = useState(null);
  // The dates clicked while the picker is open, to block ranges longer than
  // MAX_RANGE_DAYS (antd's pattern for a range limit).
  const [calendarDates, setCalendarDates] = useState(null);

  const {
    data: message,
    isLoading,
    isError,
    refetch,
  } = useMessageV2Query(orgId, messageId);

  const dateRange =
    pickedRange || (message ? getDefaultRange(message.sendTime) : null);
  const dayRange = dateRange && getDayRange(dateRange);
  const graphNoDataText = message ? getGraphNoDataText(message) : null;
  const daily = useMessageDailyV2Query(orgId, messageId, dayRange, {
    enabled: !!message && !graphNoDataText,
  });

  const goBack = () => history.push(tablePath);

  if (isLoading || isError || !message) {
    return (
      <Page>
        <Placeholder $height={400}>
          {isLoading ? (
            <CircularProgress style={{ color: BLUE }} />
          ) : (
            <>
              {isError
                ? "Couldn't load the message. Please try again."
                : 'Message not found'}
              <LinkButton onClick={goBack}>
                <ArrowBack /> Back to Messages
              </LinkButton>
            </>
          )}
        </Placeholder>
      </Page>
    );
  }

  const changeChannel = nextChannel => {
    setChannel(nextChannel);
    setMetrics(getMetricsForChannel(metrics, nextChannel));
  };

  // The last metric on the graph can't be removed (its toggle is disabled).
  const toggleMetric = metric => {
    const next = metrics.includes(metric)
      ? metrics.filter(key => key !== metric)
      : [...metrics, metric];
    setMetrics(CHANNEL_TILES[channel].filter(key => next.includes(key)));
  };

  const exportCsv = async () => {
    dispatch(fullloader(true, 'Preparing export...'));
    try {
      const [fullMessage, eventInfo, orgGroups, orgTopics] = await Promise.all([
        getMsgBy_Id(message._id, true),
        getMessageEventInfo({
          msgId: message.id,
          BmessageId: message.AB?.linkedMessageId,
        }),
        fetchOrgGroups(orgId),
        getOrganizationTopics(orgId),
      ]);
      // These services don't throw: on failure the first two return null or
      // undefined, and the last two return { error: true }.
      if (!fullMessage || !eventInfo || orgGroups.error || orgTopics.error) {
        throw new Error('A request for the export failed');
      }
      const result = getSingleMessageExport({
        organization,
        message: fullMessage,
        emailEvents: eventInfo.emailEvents,
        textEvents: eventInfo.textEvents,
        groups: createIdNameMappingFromData(orgGroups),
        topics: createIdNameMappingFromData(orgTopics),
      });
      dispatch(setProgressType('ANALYTICS_CSV'));
      dispatch(updateNavProgressMeter(100));
      dispatch(updateProgressMeterResults(result));
    } catch (e) {
      dispatch(
        toast('error', "Couldn't export the message. Please try again.")
      );
    } finally {
      dispatch(fullloader(false));
    }
  };

  const points = daily.data
    ? getDailySeries({
        days: daily.data.days,
        message,
        channel,
        metrics,
        start: dayRange.start,
        dayCount: dayRange.days,
      })
    : [];

  return (
    <Page>
      <Header
        message={message}
        dateRange={dateRange}
        // Clearing the picker restores the default range.
        onDateRangeChange={setPickedRange}
        disabledDate={date =>
          isOutsideMaxRange(date, calendarDates, MAX_RANGE_DAYS)
        }
        onCalendarChange={setCalendarDates}
        onOpenChange={isOpen => setCalendarDates(isOpen ? [null, null] : null)}
        onBack={goBack}
        onExport={exportCsv}
        onChanged={refetch}
      />
      <EngagementGraph
        channel={channel}
        metrics={metrics}
        chartType={chartType}
        onChartTypeChange={setChartType}
        points={points}
        isLoading={daily.isLoading}
        isError={daily.isError}
        noDataText={graphNoDataText}
        resends={
          message.status !== 'cancelled' && message.smartResendScheduleTime
            ? [
                {
                  time: message.smartResendScheduleTime,
                  label: getResendLabel(message.smartResendScheduleTime),
                },
              ]
            : []
        }
      />
      <StatTiles
        tiles={getTileValues(message, channel)}
        isSmallValue={
          message.status === 'scheduled' || message.status === 'cancelled'
        }
        channel={channel}
        onChannelChange={changeChannel}
        metrics={metrics}
        onToggleMetric={toggleMetric}
        onTileClick={tileKey => openTileDrawer(message, tileKey, channel)}
      />
      {drawer}
    </Page>
  );
};

export default MessageDetail;
